import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import AppLayout from './components/app-layout/index';
import Loader from './components/loader/index';
import { ProtectedRouteLayout } from './guards/index';
import AcceptInvitation from './pages/accept-invitation/index';
import AllDonePage from './pages/all-done-page/index';
import GoogleAuthCallback from './pages/auth/google-callback/index';
import { BillingEntry } from './pages/billing/billing-entry';
import { CapTableMobileDetailsPage } from './pages/cap-table/cap-table-details-page';
import { CapTableMobileStakeholderDetailsPage } from './pages/cap-table/cap-table-stakeholder-details-page';
import CapTable from './pages/cap-table/index';
import Dashboard from './pages/dashboard/index';
import { DataRoomPage } from './pages/data-room';
import {
  ActivityDataRoomsTab,
  ActivityFilesTab,
  ActivityOverviewTab,
  ActivityRoomDetailTab,
  ActivityVisitorDetailTab,
  ActivityVisitorsTab,
  DataRoomActivityPage,
} from './pages/data-room/activity';
import { WorkspacesHomePage, WorkspacesLayout } from './pages/data-room/workspaces';
import { DataRoomInvitePage } from './pages/data-room-invite/index';
import Dividends from './pages/dividends/index';
import { DocumentsSignaturesPage } from './pages/documents/signatures/document-signatures';
import { DocumentsTemplatesPage } from './pages/documents/templates/document-templates';
import Error from './pages/error/index';
import Events from './pages/events/index';
import { TransactionsList } from './pages/events/mobile/transactions-list';
import ForgotPassword from './pages/forgot-password/index';
import HangTight from './pages/hang-tight/index';
import Home from './pages/home/index';
import { Library } from './pages/library/library';
import Login from './pages/login/index';
import { ModifyGuide } from './pages/modify-guide/index';
import MyCompanies from './pages/my-companies/index';
import MyHoldings from './pages/my-holdings/index';
import Onboarding from './pages/onboarding/onboarding';
import PasswordRecovery from './pages/password-recovery/password-recovery';
import { Perks } from './pages/perks/index';
import PlanDetails from './pages/plan-details/index';
import PoolDetails from './pages/pool-details/index';
import PoolPlans from './pages/pool-plans/index';
import Profile from './pages/profile/index';
import Register from './pages/register/index';
import RegisterVerify from './pages/register-verify/index';
import Safes from './pages/safes/safes';
import { StakeholderDetailsPage } from './pages/scenario-modeling/scenario-details/mobile-details/index';
import ScenarioDetails from './pages/scenario-modeling/scenario-details/scenario-details';
import ScenarioModeling from './pages/scenario-modeling/scenario-modeling';
import Settings from './pages/settings/index';
import { SharedCapTablePage } from './pages/share/shared-cap-table-page';
import ShareClasses from './pages/share-classes/index';
import Stakeholders from './pages/stakeholders/index';
import { SubscriptionOffer } from './pages/subscription-offer/index';
import Vault from './pages/vault/vault';
import { WelcomePage } from './pages/welcome-page/index';
import { PageRoute } from './types/page.types';

const CompanySettings = lazy(() => import('./pages/company-settings/index'));
const CompanySetup = lazy(() => import('./pages/company-setup/index'));

export const router = createBrowserRouter([
  {
    path: PageRoute.GUIDES + '/:tab',
    element: <ModifyGuide />,
  },
  {
    path: PageRoute.GUIDES,
    element: <ModifyGuide />,
  },
  {
    element: <ProtectedRouteLayout />,
    children: [
      {
        path: PageRoute.HOME,
        element: (
          <AppLayout>
            <Home />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.PERKS,
        element: (
          <AppLayout>
            <Perks />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.LIBRARY,
        element: (
          <AppLayout>
            <Library />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.DOCUMENTS,
        element: (
          <AppLayout>
            <DocumentsTemplatesPage />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.DOCUMENTS_TEMPLATES,
        element: (
          <AppLayout>
            <DocumentsTemplatesPage />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.DOCUMENTS_SIGNATURES,
        element: (
          <AppLayout>
            <DocumentsSignaturesPage />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.DATA_ROOM,
        element: (
          <AppLayout>
            <DataRoomPage />
          </AppLayout>
        ),
        children: [
          {
            index: true,
            element: <Navigate replace to={PageRoute.DATA_ROOM_WORKSPACES} />,
          },
          {
            path: 'activity',
            element: <DataRoomActivityPage />,
            children: [
              {
                index: true,
                element: <ActivityOverviewTab />,
              },
              {
                path: 'data-rooms',
                element: <ActivityDataRoomsTab />,
              },
              {
                path: 'files',
                element: <ActivityFilesTab />,
              },
              {
                path: 'rooms/:dataRoomId',
                element: <ActivityRoomDetailTab />,
              },
              {
                path: 'visitors',
                element: <ActivityVisitorsTab />,
              },
              {
                path: 'visitors/:visitorKey',
                element: <ActivityVisitorDetailTab />,
              },
            ],
          },
          {
            path: 'workspaces',
            element: <WorkspacesLayout />,
            children: [
              {
                index: true,
                element: <WorkspacesHomePage />,
              },
            ],
          },
        ],
      },
      {
        path: PageRoute.PROFILE,
        element: (
          <AppLayout>
            <Profile />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.MY_COMPANIES,
        element: (
          <AppLayout>
            <MyCompanies />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.COMPANY_SETUP + '/:id',
        element: (
          <AppLayout>
            <Suspense fallback={<Loader />}>
              <CompanySetup />
            </Suspense>
          </AppLayout>
        ),
      },
      {
        path: `${PageRoute.COMPANY_SETTINGS}/:tab?`,
        element: (
          <AppLayout>
            <Suspense fallback={<Loader />}>
              <CompanySettings />
            </Suspense>
          </AppLayout>
        ),
      },
      {
        path: PageRoute.DASHBOARD,
        element: (
          <AppLayout>
            <Dashboard />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.DIVIDENDS,
        element: (
          <AppLayout>
            <Dividends />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.STAKEHOLDERS,
        element: (
          <AppLayout>
            <Stakeholders />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.SHARE_CLASSES,
        element: (
          <AppLayout className="max-lg:bg-transparent max-lg:p-0">
            <ShareClasses />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.POOL_PLANS,
        element: (
          <AppLayout>
            <PoolPlans />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.POOL_PLANS_POOL,
        element: (
          <AppLayout>
            <PoolDetails />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.POOL_PLANS_PLAN,
        element: (
          <AppLayout>
            <PlanDetails />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.EVENTS,
        element: (
          <AppLayout className="max-lg:!bg-transparent max-lg:!p-0 max-lg:!shadow-none">
            <Events />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.EVENTS + '/:id',
        element: (
          <AppLayout className="!rounded-md !px-3 !py-4 !pt-0">
            <TransactionsList />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.CAP_TABLE,
        element: (
          <AppLayout>
            <CapTable />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.CAP_TABLE + '/:id',
        element: (
          <AppLayout className="max-lg:!bg-transparent max-lg:!p-0 max-lg:!shadow-none">
            <CapTableMobileDetailsPage />
          </AppLayout>
        ),
      },

      {
        path: PageRoute.CAP_TABLE + '/:id' + '/:stakeholderType',
        element: (
          <AppLayout className="max-lg:!bg-transparent max-lg:!p-0 max-lg:!shadow-none">
            <CapTableMobileStakeholderDetailsPage />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.SAFES,
        element: (
          <AppLayout>
            <Safes />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.MY_HOLDINGS,
        element: (
          <AppLayout>
            <MyHoldings />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.WELCOME,
        element: (
          <AppLayout>
            <WelcomePage />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.SETTINGS,
        element: (
          <AppLayout>
            <Settings />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.ALL_DONE,
        element: (
          <AppLayout>
            <AllDonePage />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.HANG_TIGHT,
        element: (
          <AppLayout>
            <HangTight />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.VAULT,
        element: (
          <AppLayout>
            <Vault />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.SCENARIO_MODELING,
        element: (
          <AppLayout>
            <ScenarioModeling />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.SCENARIO_DETAILS,
        element: (
          <AppLayout>
            <ScenarioDetails />
          </AppLayout>
        ),
      },
      {
        path: PageRoute.SCENARIO_DETAILS + '/:name',
        element: (
          <AppLayout className="max-lg:!bg-transparent max-lg:!p-0 max-lg:!shadow-none">
            <StakeholderDetailsPage />
          </AppLayout>
        ),
      },
    ],
  },

  {
    path: PageRoute.ACCEPT_INVITATION,
    element: <AcceptInvitation />,
  },
  {
    // Public, unauthenticated: whoever follows an invitation link has no account yet, so this
    // sits outside ProtectedRouteLayout exactly as the stakeholder invitation does.
    path: PageRoute.DATA_ROOM_INVITE,
    element: <DataRoomInvitePage />,
  },
  {
    // Public, unauthenticated: viewers reach a shared cap table by link alone, so this must stay
    // outside ProtectedRouteLayout and outside AppLayout chrome.
    path: PageRoute.SHARED_CAP_TABLE,
    element: <SharedCapTablePage />,
  },
  {
    // Public dispatcher: company UUID → authenticated checkout; offer slug → public offer landing.
    path: PageRoute.BILLING + '/:id',
    element: <BillingEntry />,
  },
  {
    path: PageRoute.SUBSCRIPTION_OFFER_ENTERPRISE,
    element: <SubscriptionOffer />,
  },
  {
    path: PageRoute.SUBSCRIPTION_OFFER_GROWTH,
    element: <SubscriptionOffer />,
  },
  {
    path: PageRoute.SUBSCRIPTION_OFFER_STARTUP,
    element: <SubscriptionOffer />,
  },
  {
    path: PageRoute.AUTH_GOOGLE_CALLBACK,
    element: <GoogleAuthCallback />,
  },
  {
    path: PageRoute.LOGIN,
    element: <Login />,
  },
  {
    path: PageRoute.REGISTER,
    element: <Register />,
  },
  {
    path: PageRoute.REGISTER_VERIFY,
    element: <RegisterVerify />,
  },
  {
    path: PageRoute.FORGOT_PASSWORD,
    element: <ForgotPassword />,
  },
  {
    path: PageRoute.PASSWORD_RECOVERY,
    element: <PasswordRecovery />,
  },

  {
    path: PageRoute.ONBOARDING,
    element: (
      <AppLayout>
        <Onboarding />
      </AppLayout>
    ),
  },

  {
    path: PageRoute.ERROR,
    element: <Error />,
  },
]);
